import { ActionReference, DefaultGameState, GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";
import { InventoryComponent } from "./InventoryComponent";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    :host {
        font-family: "Onesize";
        width: 100%;
        max-width: 1024px;
        height: 100%;
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto calc(35vh + 10px) minmax(calc(35vh + 10px), 1fr) auto;
        grid-column-gap: 0px;
        grid-row-gap: 0px;
    }

    .title {
        text-align: center;
        margin-top: 10px;
        overflow: auto;
    }

    .header {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        margin-top: 10px;
    }

    .header img {
        width: auto;
        height: 100%;
        image-rendering: pixelated;
    }

    .header img:nth-child(n + 2) {
        position: absolute;
    }

    .content {
        flex-grow: 1;
        overflow: auto;
        margin-top: 10px;
        padding: 0 10px;
    }

    .content p {
        margin: 0 0 0 0;
        background-color: rgba(0, 0, 0, 0.6);
    }

    .content p:last-of-type {
        margin: 0;
    }

    .footer {
        position: fixed;
        bottom: 0;
        left: 5%;
        width: 90%;
        border-radius: 10px 10px 0 0;
        background-color: #898a8c58;
        max-height: 200px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }

    .footer .buttons {
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        padding: 10px;
        gap: 5px;
    }

    .footer .button {
        background-image: url("/assets/img/ui/button_gray.png");
        background-size: 100% 100%;
        background-position: center;
        background-repeat: no-repeat;

        padding: 8px 14px;

        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;

        color: white;
    }

    .footer .action-buttons,
    .footer .game-object-buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    .footer .button.active,
    .footer .button:hover {
        background-image: url("/assets/img/ui/button_gray_hover.png");
    }

    .footer .action-button {
        background-image: url("/assets/img/ui/button.png");
        background-size: 100% 100%;
        background-position: center;
        background-repeat: no-repeat;

        padding: 8px 14px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
        color: white;
    }

    .footer .action-button:hover,
    .footer .action-button.active {
        background-image: url("/assets/img/ui/button_hover.png");
    }

    .footer .object-button {
        background-image: url("/assets/img/ui/button_gray.png");
        background-size: 100% 100%;
        background-position: center;
        background-repeat: no-repeat;

        padding: 8px 16px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
        color: white;
    }

    .footer .object-button:hover,
    .footer .object-button.active {
        background-image: url("/assets/img/ui/button_gray_hover.png");
    }

    .footer-top-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 6px 10px 0 10px;
    }

    .inventory-button {
        display: flex;
        align-items: center;
    }

    .menu-button {
        position: fixed;
        top: 0;
        right: 10px;
        z-index: 1000;
    }
`;

const mobileStyles: string = css`
    @media (max-width: 768px) {
        :host {
            grid-template-rows: auto calc(25vh + 10px) minmax(calc(25vh + 10px), 1fr) auto;
        }

        .title {
            font-size: 0.9rem;
            margin-top: 6px;
        }

        .content {
            margin-top: 16px;
            padding: 0 8px;
            font-size: 0.9rem;
        }

        .footer {
            left: 0;
            width: 100%;
            border-radius: 10px 10px 0 0;
            max-height: 220px;
        }

        .footer .buttons {
            padding: 8px;
            gap: 4px;
        }

        .footer .action-buttons,
        .footer .game-object-buttons {
            gap: 6px;
        }

        .footer .action-button,
        .footer .object-button,
        .footer .button {
            padding: 6px 10px;
            font-size: 0.8rem;
        }

        .footer-top-row {
            padding: 4px 8px 0 8px;
        }

        .inventory-button .button,
        .menu-button .button {
            font-size: 0.8rem;
            padding: 5px 10px;
        }
    }

    @media (max-width: 480px) {
        :host {
            grid-template-rows: auto calc(20vh + 10px) minmax(calc(20vh + 10px), 1fr) auto;
        }

        .title {
            font-size: 0.8rem;
        }

        .content {
            font-size: 0.85rem;
            padding: 0 6px;
        }

        .footer {
            max-height: 180px;
        }

        .footer .action-buttons,
        .footer .game-object-buttons {
            gap: 4px;
        }

        .footer .action-button,
        .footer .object-button,
        .footer .button {
            padding: 5px 8px;
            font-size: 0.75rem;
        }

        .inventory-button .button,
        .menu-button .button {
            font-size: 0.75rem;
            padding: 4px 8px;
        }
    }
`;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    /**
     * The "constructor" of a Web Component
     * Attaches a shadow root, mounts the {@link InventoryComponent} modal onto the
     * document body (if not already present), and fetches the initial game state.
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        if (!document.getElementById("game-inventory")) {
            const inv: InventoryComponent = document.createElement("game-inventory") as InventoryComponent;
            inv.id = "game-inventory";
            document.body.appendChild(inv);
        }

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
                ${mobileStyles}
            </style>

            ${this.renderTitle()}
            ${this.renderHeader()}
            ${this.renderContent()}
            ${this.renderFooter()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `<div class="title">${roomName}</div>`;
        }

        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderHeader(): string {
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;

        if (roomImages && roomImages.length > 0) {
            document.body.style.backgroundImage = roomImages
                .map(url => `url('/assets/img/rooms/${url}.png')`)
                .join(", ");
            document.body.style.backgroundSize = "100% auto";
            document.body.style.backgroundPosition = "center";
            document.body.style.backgroundRepeat = "no-repeat";
            document.body.style.backgroundAttachment = "fixed";

            return "";
        }

        else {
            document.body.style.backgroundImage = "url('/assets/img/rooms/default.jpg')";
            document.body.style.backgroundSize = "100% auto";
            document.body.style.backgroundPosition = "center";
            document.body.style.backgroundRepeat = "no-repeat";
            document.body.style.backgroundAttachment = "fixed";

            return "";
        }
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        const formatText: (input: string) => string = (input: string): string => {
            // Replace the custom Markdown syntax with HTML tags
            return input
                // Bold tussen ` `
                .replace(/`([^']+)`/g, "<strong>$1</strong>")
                // Italic tussen ~ ~
                .replace(/~([^~]+)~/g, "<em>$1</em>")
                // Italic en bold tussen `~ ~`
                .replace(/`~([^~]+)~`/g, "<strong><em>$1</em></strong>")
                // Line breaks na \n
                .replace(/\n/g, "<br>");
        };

        return `
        <div class="content">
            ${this._currentGameState?.text.map(text => `<p>${formatText(text)}</p>`).join("") || ""}
        </div>
    `;
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        return html`
            <div class="footer">
                <div class="footer-top-row">
                    <div class="inventory-button">${this.renderInventoryButton()}</div>
                </div>
                <div class="buttons">
                    <div class="action-buttons">
                        ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}
                    </div>
                    <div class="game-object-buttons">
                        ${this._selectedActionButton
                ? this._currentGameState?.objects.map(button => this.renderGameObjectButton(button)) || ""
                : ""
            }
                    </div>
                    <div class="menu-button">${this.renderMenuButton()}</div>
                </div>
            </div>
        `;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="action-button ${this._selectedActionButton === button ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => void this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="object-button ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => void this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            this.render();

            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state.
        this.updateGameState(state);
    }

    /**
     * Render the menu button (top-right). Clicking it now opens the pause menu
     * via the shared GameEventService (consistent with how SwitchPageActionResult works).
     * The old reload behaviour has been removed.
     *
     * @returns HTML element of the menu button
     */
    private renderMenuButton(): HTMLElement {
        const element: HTMLElement = html`
            <a class="button">
                Menu
            </a>
        `;

        element.addEventListener("click", () => {
            this._gameEventService.switchPage(Page.PauseMenu);
        });

        return element;
    }

    /**
     * Render the inventory button element.
     *
     * Opens the {@link InventoryComponent} modal when clicked, which fetches and
     * displays all items currently in the player's inventory.
     *
     * @returns HTML element of the inventory button
     * @author Julie Kraanen
    */
    private renderInventoryButton(): HTMLElement {
        const element: HTMLElement = html`
            <a class="button">
                Inventory
            </a>
        `;

        element.addEventListener("click", () => {
            const inventory: InventoryComponent | null = document.getElementById("game-inventory");

            if (inventory && typeof inventory.open === "function") {
                void inventory.open();
            }
        });

        return element;
    }
}
